/**
 * Utilidades para helpers comunes de componentes
 * Centraliza patrones repetidos en componentes Angular
 */

import { Subscription } from 'rxjs';
import { LoaderService } from '@app/services/general/loader.service';

/**
 * Configuración para el loader de un componente
 */
export interface LoaderSetup {
  isLoading: boolean;
  loaded: boolean;
}

/**
 * Configura el loader de un componente de forma estándar
 *
 * @param loaderService - Servicio de loader
 * @param subscriptions - Subscription para agregar la suscripción
 * @param callback - Callback que recibe los estados de loading y loaded
 *
 * @example
 * ```typescript
 * setupComponentLoader(
 *   this.loaderService,
 *   this.subscriptions,
 *   ({ loading, loaded }) => {
 *     this.isLoading = loading;
 *     this.loaded = loaded;
 *   }
 * );
 * ```
 */
export function setupComponentLoader(
  loaderService: LoaderService,
  subscriptions: Subscription,
  callback: (state: { loading: boolean; loaded: boolean }) => void
): void {
  loaderService.show();
  subscriptions.add(
    loaderService.loading$.subscribe(({ loading, loaded }) => {
      callback({ loading, loaded });
    })
  );
}

/**
 * Obtiene el session_id del localStorage
 * @returns Session ID como número, o 0 si no existe
 */
export function getSessionId(): number {
  return Number(localStorage.getItem('session_id')) || 0;
}

/**
 * Scroll to top de la página
 */
export function scrollToTop(): void {
  window.scrollTo(0, 0);
}

/**
 * Valida si un campo del formulario es inválido y ha sido tocado
 * @param form - Formulario
 * @param fieldName - Nombre del campo
 * @returns true si el campo es inválido y tocado
 */
export function isInvalidField(form: any, fieldName: string): boolean {
  const control = form.get(fieldName);
  if (!control) return false;
  return control.errors && control.touched;
}

/**
 * Marca todos los campos de un formulario como tocados
 * Útil para mostrar errores de validación al hacer submit
 * @param form - Formulario
 */
export function markFormAsTouched(form: any): void {
  form.markAllAsTouched();
}
